import { FC, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Chip, Input } from '@ds';
import { AdminFormRow, AdminFormSection } from '@ui';
import { TMDXFile } from '@/api/apis/files';
import { OrganType } from '@/api/apis/case';
import { ocsConsoleStatusName } from '@/constants/resources.constants';
import { ControlledTextInput } from '../../components/inputs/ControlledTextInput';
import { NewOcsConsoleForm } from '../types';
import { NopHubSelector } from './NopHubSelector';
import { OcsConsoleMaintenanceRow } from './OcsConsoleMaintenanceRow';
import { OcsConsoleOrganSelector } from './OcsConsoleOrganSelector';
import { OcsConsoleTrialProgramRow } from './OcsConsoleTrialProgramRow';

type OcsConsoleFormProps = {
  type?: 'edit' | 'create';
  isReadOnly?: boolean;
  originalOrganType?: OrganType;
};

export const OcsConsoleForm: FC<OcsConsoleFormProps> = ({ type = 'create', isReadOnly = false, originalOrganType }) => {
  const { control, trigger, setValue } = useFormContext<NewOcsConsoleForm>();
  const logoPath = useWatch({ control, name: 'logoPath' });
  const status = useWatch({ control, name: 'status' });
  const caseName = useWatch({ control, name: 'caseName' });

  useEffect(() => {
    if (type === 'edit') {
      trigger();
    }
  }, [type, trigger]);

  const onImageChange = (file: TMDXFile) => {
    setValue('image', file);
  };

  return (
    <AdminFormSection
      title="OCS Console Details"
      withAvatar
      imageUrl={logoPath}
      onImageChange={onImageChange}
      isReadOnly={isReadOnly}
      type="device"
    >
      <AdminFormRow title="OCS Console Name" isRequired>
        <ControlledTextInput name="ocsConsoleName" aria-label="OCS Console Name" isDisabled={isReadOnly} />
      </AdminFormRow>
      <OcsConsoleOrganSelector isReadOnly={isReadOnly} originalOrganType={originalOrganType} />
      <AdminFormRow title="Serial Number" isRequired>
        <ControlledTextInput name="serialNumber" aria-label="Serial Number" isDisabled={isReadOnly} />
      </AdminFormRow>
      <NopHubSelector isReadOnly={isReadOnly} />
      <AdminFormRow title="Status">
        <Input
          aria-label="Status"
          type="text"
          isDisabled
          startContent={
            status ? (
              <Chip size="sm" className="mr-2">
                {ocsConsoleStatusName[status]}
              </Chip>
            ) : (
              <Chip size="sm" className="mr-2">
                Available
              </Chip>
            )
          }
        />
      </AdminFormRow>
      <AdminFormRow title="Case ID">
        <Input aria-label="Case ID" type="text" value={caseName || ''} isDisabled />
      </AdminFormRow>
      <OcsConsoleMaintenanceRow isReadOnly={isReadOnly} />
      <OcsConsoleTrialProgramRow isReadOnly={isReadOnly} />
    </AdminFormSection>
  );
};
